import * as models from '@app/models/index';
import Injector from '@app/services';
import { AccountService } from '@app/services/account.service';
import { IntlService } from '@app/services/intl.service';
import { SystemConfigService } from '@app/services/system-config.service';
import { Grid, GridList, GridListTile, GridListTileBar, TextField, Typography } from '@material-ui/core';
import { Field, Form, Formik, FormikProps, FormikValues } from 'formik';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { injectIntl } from 'react-intl';
import * as Yup from 'yup';
import { IUPSNativeConfiguration } from '../../models/carrier.models';
import withSnackBar from '../core/message.component';
import { getUpsNativeConfigurationSchema } from './carriers.schema';
import * as _ from 'lodash';

const UPSNativeConfigurationFormFactory: IUPSNativeConfiguration = {
	accountNumber: '',
	bookNumber: '',
	pageNumber: '',
	sequenceNumberStart: 0,
	sequenceNumberEnd: 0,
	sequenceNumber: 0,
};
const accountService: AccountService = Injector.get(AccountService);
const systemConfigService: SystemConfigService = Injector.get(SystemConfigService);
const intlService: IntlService = Injector.get(IntlService);

let formValues: IUPSNativeConfiguration = null;
let sequenceNumberRange: models.ISequenceNumberRange;

const UPSNativeComponent: React.FunctionComponent<models.IUSPSePostageProps> = forwardRef(
	(props, ref: React.MutableRefObject<any>) => {
		const formRef: React.MutableRefObject<Formik> = useRef(null);

		const [upsNativeConfigurationSchema, setUPSNativeConfigurationSchema] = useState<any>(null);
		const [existingFormValue, setExistingFormValue] = useState(props.preloadedData);
		const [upsNativeConfigurationForm, setUPSNativeConfigurationFormFactory] = useState<IUPSNativeConfiguration>(
			UPSNativeConfigurationFormFactory
		);

		useEffect(() => {
			async function getSequenceNumberRange() {
				try {
					sequenceNumberRange = await systemConfigService.getSequenceNumberRange();
					setUPSNativeConfigurationSchema(getUpsNativeConfigurationSchema(sequenceNumberRange, props.preloadedData));
					setUPSNativeConfigurationFormFactory({
						...upsNativeConfigurationForm,
						sequenceNumberEnd: sequenceNumberRange.sequenceNumberMax,
						sequenceNumberStart: sequenceNumberRange.sequenceNumberMin,
						sequenceNumber: sequenceNumberRange.sequenceNumberMin,
					});
				} catch (error) {
					throw error;
				}
			}

			getSequenceNumberRange();

			// Get seqence number
			getSequenceNumber();
		}, []);

		useImperativeHandle(ref, () => ({
			getFormValues: async () => {
				try {
					await formRef.current.submitForm();
					const errors = Object.keys(formRef.current.state.errors).length;
					if (!errors) {
						return formValues;
					}
					return null;
				} catch (err) {
					throw err;
				}
			},
		}));

		const getSequenceNumber = async () => {
			if (props.preloadedData == null || props.preloadedData.carrierAccountId == null) {
				return;
			}

			try {
				const result = await accountService.GetSequenceNumber(props.preloadedData.carrierAccountId);
				setExistingFormValue({
					...existingFormValue,
					sequenceNumber: result.sequenceNumber,
				});
			} catch (err) {
				throw err;
			}
		};

		return (
			<Grid container={true} justifyContent='center' alignItems='center'>
				{props.selectedCarrierLogo && (
					<GridList cols={1} className='selected-carrier-tile'>
						<GridListTile>
							<div className='carriers-tile'>
								<img src={props.selectedCarrierLogo} className='carrier-img' />
							</div>
							<GridListTileBar
								style={{
									backgroundColor: 'transparent',
									height: '30px',
								}}
								title={
									<Grid container={true} justifyContent='center' alignItems='center'>
										<Grid item={true}>
											<Typography variant='caption' color='textSecondary' noWrap={true}>
												<strong>{props.selectedCarrierName}</strong>
											</Typography>
										</Grid>
									</Grid>
								}
							/>
						</GridListTile>
					</GridList>
				)}
				<Grid item={true} sm={12} md={12} lg={12} xl={12} className='mt-2'>
					<Formik
						initialValues={_.isEmpty(existingFormValue) ? upsNativeConfigurationForm : existingFormValue}
						onSubmit={() => {}}
						validationSchema={upsNativeConfigurationSchema}
						enableReinitialize={true}
						ref={formRef}
					>
						{(formikProps: FormikProps<FormikValues>) => {
							// assigning form values to an outside variable
							formValues = formikProps.values as IUPSNativeConfiguration;

							return (
								<Form noValidate={true} className='width-100'>
									<Grid container={true} justifyContent='flex-start' alignItems='center' spacing={2}>
										<Grid item={true} sm={6} md={6} lg={6} xl={6}>
											<Field
												name='accountNumber'
												label={props.intl.formatMessage({
													id: 'app.carrier-account-number-label',
												})}
												variant='outlined'
												component={TextField}
												type='text'
												fullWidth={true}
												required={true}
												value={formikProps.values.accountNumber}
												onChange={(e: React.ChangeEvent) => formikProps.handleChange('accountNumber')(e)}
												error={
													!!(
														(formikProps.values.accountNumber.length || formikProps.submitCount) &&
														!!formikProps.errors.accountNumber
													)
												}
												helperText={
													formikProps.values.accountNumber.length || formikProps.submitCount
														? formikProps.errors.accountNumber
														: null
												}
											/>
										</Grid>
										<Grid item={true} sm={6} md={6} lg={6} xl={6}>
											<Field
												name='bookNumber'
												label={props.intl.formatMessage({
													id: 'app.carrier-book-number-label',
												})}
												variant='outlined'
												component={TextField}
												type='text'
												fullWidth={true}
												required={true}
												value={formikProps.values.bookNumber}
												onChange={(e: React.ChangeEvent) => formikProps.handleChange('bookNumber')(e)}
												error={
													!!(
														(formikProps.values.bookNumber.length || formikProps.submitCount) &&
														!!formikProps.errors.bookNumber
													)
												}
												helperText={
													formikProps.values.bookNumber.length || formikProps.submitCount
														? formikProps.errors.bookNumber
														: null
												}
											/>
										</Grid>
										<Grid item={true} sm={6} md={6} lg={6} xl={6}>
											<Field
												name='pageNumber'
												label={props.intl.formatMessage({
													id: 'app.carrier-page-number-label',
												})}
												variant='outlined'
												component={TextField}
												type='number'
												fullWidth={true}
												required={true}
												value={formikProps.values.pageNumber}
												onChange={(e: React.ChangeEvent) => formikProps.handleChange('pageNumber')(e)}
												error={
													!!(
														(formikProps.values.pageNumber.length || formikProps.submitCount) &&
														!!formikProps.errors.pageNumber
													)
												}
												helperText={
													formikProps.values.pageNumber.length || formikProps.submitCount
														? formikProps.errors.pageNumber
														: null
												}
											/>
										</Grid>
										<Grid item={true} sm={6} md={6} lg={6} xl={6}>
											<Field
												name='sequenceNumber'
												label={props.intl.formatMessage({
													id: 'app.carrier-sequence-number-label',
												})}
												variant='outlined'
												component={TextField}
												type='number'
												fullWidth={true}
												required={true}
												value={formikProps.values.sequenceNumber}
												onChange={(e: React.ChangeEvent) => formikProps.handleChange('sequenceNumber')(e)}
												error={
													!!(
														(formikProps.values.sequenceNumber.length || formikProps.submitCount) &&
														!!formikProps.errors.sequenceNumber
													)
												}
												helperText={
													formikProps.values.sequenceNumber.length || formikProps.submitCount
														? formikProps.errors.sequenceNumber
														: null
												}
											/>
										</Grid>
										<Grid item={true} sm={6} md={6} lg={6} xl={6}>
											<Field
												name='sequenceNumberStart'
												label={props.intl.formatMessage({
													id: 'app.carrier-sequence-number-start-label',
												})}
												variant='outlined'
												component={TextField}
												type='number'
												fullWidth={true}
												required={true}
												value={formikProps.values.sequenceNumberStart}
												onChange={(e: any) => {
													formikProps.handleChange('sequenceNumberStart')(e);
													setUPSNativeConfigurationSchema(
														upsNativeConfigurationSchema.concat(
															Yup.object().shape<any>({
																sequenceNumber: Yup.number().min(
																	e.target.value,
																	intlService.GetFormattedMessage(
																		'app.carrier-sequence-number-range-current-from-start',
																		{ 0: e.target.value }
																	)
																),
															})
														)
													);
												}}
												error={
													!!(
														(formikProps.values.sequenceNumberStart.length || formikProps.submitCount) &&
														!!formikProps.errors.sequenceNumberStart
													)
												}
												helperText={
													formikProps.values.sequenceNumberStart.length || formikProps.submitCount
														? formikProps.errors.sequenceNumberStart
														: null
												}
											/>
										</Grid>
										<Grid item={true} sm={6} md={6} lg={6} xl={6}>
											<Field
												name='sequenceNumberEnd'
												label={props.intl.formatMessage({
													id: 'app.carrier-sequence-number-end-label',
												})}
												variant='outlined'
												component={TextField}
												type='number'
												fullWidth={true}
												required={true}
												value={formikProps.values.sequenceNumberEnd}
												onChange={(e: any) => {
													formikProps.handleChange('sequenceNumberEnd')(e);
													setUPSNativeConfigurationSchema(
														upsNativeConfigurationSchema.concat(
															Yup.object().shape<any>({
																sequenceNumber: Yup.number().max(
																	e.target.value,
																	intlService.GetFormattedMessage(
																		'app.carrier-sequence-number-range-current-from-end',
																		{ 0: e.target.value }
																	)
																),
															})
														)
													);
												}}
												error={
													!!(
														(formikProps.values.sequenceNumberEnd.length || formikProps.submitCount) &&
														!!formikProps.errors.sequenceNumberEnd
													)
												}
												helperText={
													formikProps.values.sequenceNumberEnd.length || formikProps.submitCount
														? formikProps.errors.sequenceNumberEnd
														: null
												}
											/>
										</Grid>
									</Grid>
								</Form>
							);
						}}
					</Formik>
				</Grid>
			</Grid>
		);
	}
);

export default withSnackBar(injectIntl(UPSNativeComponent, { withRef: true }));



// WEBPACK FOOTER //
// ./src/components/carriers/ups-native.component.tsx